/**
 * useNotificationPolling — polls deposits, withdrawals and bets for status changes.
 *
 * - Runs only when the user is logged in (token present).
 * - Seeds state immediately on login (no toast on first load).
 * - Polls every 30s (deposits/withdrawals) and 60s (bets).
 * - On status change: shows a toast + refreshes wallet + emits a refresh event
 *   so any mounted UI component can reload its data instantly.
 * - Stops and resets all state on logout.
 */

import { useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../config';
import { accountEvents } from './accountEvents';

export function useNotificationPolling({ accessToken, fetchWallet }) {
  const prevDeposits    = useRef({});
  const prevWithdrawals = useRef({});
  const prevBets        = useRef({});

  const fetchWalletRef  = useRef(fetchWallet);
  fetchWalletRef.current = fetchWallet;

  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${accessToken}`,
  }), [accessToken]);

  const get = useCallback(async (url) => {
    const res = await fetch(`${API_BASE_URL}${url}`, {
      credentials: 'include',
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  }, [authHeaders]);

  /* ── Deposits ── */
  const pollDeposits = useCallback(async () => {
    try {
      const deposits = await get('/api/v1/deposits');
      let shouldRefresh = false;

      deposits.forEach((dep) => {
        const prev = prevDeposits.current[dep.id];
        if (prev === undefined) {
          prevDeposits.current[dep.id] = dep.status;
          return;
        }
        if (prev === 'pending' && dep.status === 'approved') {
          toast.success(
            `✅ Votre dépôt de ${parseFloat(dep.amount).toLocaleString('fr-FR')} FC a été approuvé — solde crédité !`,
            { toastId: `dep-approved-${dep.id}` }
          );
          fetchWalletRef.current?.();
          shouldRefresh = true;
        }
        if (prev === 'pending' && dep.status === 'rejected') {
          toast.error(
            `❌ Votre dépôt de ${parseFloat(dep.amount).toLocaleString('fr-FR')} FC a été rejeté${dep.admin_comment ? ` — ${dep.admin_comment}` : ''}`,
            { toastId: `dep-rejected-${dep.id}` }
          );
          shouldRefresh = true;
        }
        prevDeposits.current[dep.id] = dep.status;
      });

      if (shouldRefresh) accountEvents.emit('deposits:refresh');
    } catch {
      // Silent
    }
  }, [get]);

  /* ── Withdrawals ── */
  const pollWithdrawals = useCallback(async () => {
    try {
      const withdrawals = await get('/api/v1/withdrawals');
      let shouldRefresh = false;

      withdrawals.forEach((wd) => {
        const prev = prevWithdrawals.current[wd.id];
        if (prev === undefined) {
          prevWithdrawals.current[wd.id] = wd.status;
          return;
        }
        if (prev === 'pending' && wd.status === 'paid') {
          toast.success(
            `💸 Votre retrait de ${parseFloat(wd.amount).toLocaleString('fr-FR')} FC a été payé${wd.admin_reference ? ` — Réf : ${wd.admin_reference}` : ''}`,
            { toastId: `wd-paid-${wd.id}` }
          );
          fetchWalletRef.current?.();
          shouldRefresh = true;
        }
        prevWithdrawals.current[wd.id] = wd.status;
      });

      if (shouldRefresh) accountEvents.emit('withdrawals:refresh');
    } catch {
      // Silent
    }
  }, [get]);

  /* ── Bets ── */
  const pollBets = useCallback(async () => {
    try {
      const bets = await get('/api/v1/bets');
      let shouldRefresh = false;

      bets.forEach((bet) => {
        const prev = prevBets.current[bet.id];
        if (prev === undefined) {
          prevBets.current[bet.id] = bet.status;
          return;
        }
        if (prev === 'pending' && bet.status === 'won') {
          toast.success(
            `🏆 Félicitations ! Votre pari a gagné — ${parseFloat(bet.potential_win).toLocaleString('fr-FR')} FC crédités !`,
            { toastId: `bet-won-${bet.id}` }
          );
          fetchWalletRef.current?.();
          shouldRefresh = true;
        }
        if (prev === 'pending' && bet.status === 'lost') {
          toast.error(
            `😔 Votre pari de ${parseFloat(bet.stake).toLocaleString('fr-FR')} FC est perdu`,
            { toastId: `bet-lost-${bet.id}` }
          );
          shouldRefresh = true;
        }
        prevBets.current[bet.id] = bet.status;
      });

      if (shouldRefresh) accountEvents.emit('bets:refresh');
    } catch {
      // Silent
    }
  }, [get]);

  /* ── Lifecycle ── */
  useEffect(() => {
    if (!accessToken) {
      prevDeposits.current    = {};
      prevWithdrawals.current = {};
      prevBets.current        = {};
      return;
    }

    // Seed immediately — no toasts, just initialise previous state
    pollDeposits();
    pollWithdrawals();
    pollBets();

    const depositTimer    = setInterval(pollDeposits,    30_000);
    const withdrawalTimer = setInterval(pollWithdrawals, 30_000);
    const betTimer        = setInterval(pollBets,        60_000);

    return () => {
      clearInterval(depositTimer);
      clearInterval(withdrawalTimer);
      clearInterval(betTimer);
    };
  }, [accessToken, pollDeposits, pollWithdrawals, pollBets]);
}
